import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  ViewEncapsulation,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DateAdapter, provideNativeDateAdapter } from '@angular/material/core';
import { MatCalendar } from '@angular/material/datepicker';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FieldKind, type DocField } from '../../_lib/docx-core';
import { SnackbarType } from '../../_enums/snackbar-type.enum';
import { InlineSnackbar } from '../inline-snackbar/inline-snackbar';
import { Waiting } from '../waiting/waiting';
import { DocStore } from './doc-store';
import { EditorDateAdapter, UNKNOWN_DATE, dateFromIso, isoFromDate } from './editor-date-adapter';
import { FieldPanel, type FocusedField } from './field-panel';
import { bindFields, showValue } from './field-overlay';

interface Chooser {
  readonly field: DocField;
  readonly left: number;
  readonly top: number;
}

const DEFAULT_LANGUAGE = 'nl-NL';

// Room the date chooser needs (Material calendar plus border), used to keep it on screen.
const CALENDAR_WIDTH = 296;
const CALENDAR_HEIGHT = 350;
const VIEWPORT_MARGIN = 8;

@Component({
  selector: 'app-docx-editor',
  imports: [TranslateModule, FieldPanel, InlineSnackbar, Waiting, MatCalendar],
  // The date pickers in the panel and the preview share this adapter, so both speak
  // the language of the app instead of the browser's.
  providers: [DocStore, provideNativeDateAdapter(), { provide: DateAdapter, useClass: EditorDateAdapter }],
  templateUrl: './docx-editor.html',
  styleUrl: './docx-editor.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocxEditor {
  public readonly file = input.required<File>();
  public readonly edited = output<File>();

  protected readonly store = inject(DocStore);
  protected readonly chooser = signal<Chooser | null>(null);
  protected readonly focused = signal<FocusedField | null>(null);
  protected readonly kind = FieldKind;
  protected readonly snackbarType = SnackbarType;

  private readonly stage = viewChild.required<ElementRef<HTMLElement>>('stage');
  private readonly preview = viewChild.required<ElementRef<HTMLElement>>('preview');
  private readonly styles = viewChild.required<ElementRef<HTMLElement>>('styles');

  private emitted: File | null = null;

  constructor() {
    effect(() => {
      const file = this.file();

      untracked(() => void this.load(file));
    });

    this.followAppLanguage();
  }

  protected async refresh(): Promise<void> {
    this.chooser.set(null);

    // Every change re-renders the preview, which first empties it. The stage would
    // collapse and scroll back to the top; holding the preview's height and restoring
    // the scroll position keeps the reader where they were.
    const stage = this.stage().nativeElement;
    const preview = this.preview().nativeElement;
    const { scrollTop, scrollLeft } = stage;
    preview.style.minHeight = `${preview.offsetHeight}px`;

    try {
      const anchors = await this.store.render(preview, this.styles().nativeElement);

      bindFields(anchors, this.store.fields(), {
        commit: (id, text) => this.set(id, text),
        toggle: (id) => this.set(id, !this.valueOf(id)),
        choose: (id, host) => this.openChooser(id, host),
        focus: (id) => this.focused.set({ id }),
        type: (id, text) => this.store.setValue(id, text),
      });
    } finally {
      preview.style.minHeight = '';
      stage.scrollTop = scrollTop;
      stage.scrollLeft = scrollLeft;
    }
  }

  protected onChanged(): void {
    this.publish();

    void this.refresh();
  }

  protected onTyped(id: string): void {
    showValue(this.preview().nativeElement, id, this.asText(this.valueOf(id)));
  }

  protected chooseOption(value: string): void {
    const open = this.chooser();
    if (!open) {
      return;
    }

    this.set(open.field.id, value);
  }

  protected closeChooser(): void {
    this.chooser.set(null);
  }

  protected asText(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  // A date field holding text such as "Onbekend" opens the calendar without a selection.
  protected dateOf(value: unknown): Date | null {
    return dateFromIso(value);
  }

  protected chooseDate(date: Date | null): void {
    if (date) {
      this.chooseOption(isoFromDate(date));
    }
  }

  // Back to "Onbekend", as Mynte writes a date it could not fill.
  protected clearDate(): void {
    this.chooseOption(UNKNOWN_DATE);
  }

  private async load(file: File): Promise<void> {
    if (file === this.emitted) {
      return;
    }

    await this.store.open(file);

    if (this.store.isOpen()) {
      await this.refresh();
    }
  }

  private set(id: string, value: string | boolean): void {
    this.store.setValue(id, value);
    this.onChanged();
  }

  private publish(): void {
    const file = this.store.toFile();
    if (!file) {
      return;
    }

    this.emitted = file;
    this.edited.emit(file);
  }

  private valueOf(id: string): string | boolean {
    return this.store.fields().find((f) => f.id === id)?.value ?? '';
  }

  private openChooser(id: string, host: HTMLElement): void {
    const field = this.store.fields().find((f) => f.id === id);
    if (!field) {
      return;
    }

    const box = host.getBoundingClientRect();
    if (field.kind !== FieldKind.Date) {
      this.chooser.set({ field, left: box.left, top: box.bottom });

      return;
    }

    // The calendar is tall: open it above the field when there is no room below,
    // and keep it inside the window horizontally.
    const fitsBelow = box.bottom + CALENDAR_HEIGHT <= window.innerHeight - VIEWPORT_MARGIN;
    const top = fitsBelow ? box.bottom : Math.max(VIEWPORT_MARGIN, box.top - CALENDAR_HEIGHT);
    const left = Math.max(
      VIEWPORT_MARGIN,
      Math.min(box.left, window.innerWidth - CALENDAR_WIDTH - VIEWPORT_MARGIN),
    );

    this.chooser.set({ field, left, top });
  }

  // Month and weekday names follow the language chosen in the app (nl-NL or en-US),
  // also when the user switches language while the editor is open.
  private followAppLanguage(): void {
    const dateAdapter = inject(DateAdapter<Date>);
    const translate = inject(TranslateService);

    dateAdapter.setLocale(translate.getCurrentLang() || DEFAULT_LANGUAGE);

    translate.onLangChange
      .pipe(takeUntilDestroyed(inject(DestroyRef)))
      .subscribe(({ lang }) => dateAdapter.setLocale(lang));
  }
}
